import { useState, useMemo } from 'react'
import { SectionHeader, NumberField, SelectField, AgronomistNote } from '@/components/ui/CalcUI'

type AgeProfile = 'young' | 'mature' | 'peak'
type PriceUnit = 'kg' | 'ton'

const AGE_PROFILES = [
  { value: 'young', label: 'Young (3–5 years)', yield: 25, color: '#16a34a' },
  { value: 'mature', label: 'Mature (6–10 years)', yield: 100, color: '#d97706' },
  { value: 'peak', label: 'Peak Production (11–40 years)', yield: 220, color: '#ea580c' },
]

const PRICE_UNIT_OPTIONS = [
  { value: 'kg', label: 'USD per kg' },
  { value: 'ton', label: 'USD per Metric Ton' },
]

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${value.toFixed(2)}`
}

export default function YieldRevenue() {
  const [treeCount, setTreeCount] = useState(100)
  const [ageProfile, setAgeProfile] = useState<AgeProfile>('mature')
  const [priceUnit, setPriceUnit] = useState<PriceUnit>('kg')
  const [marketPrice, setMarketPrice] = useState(1.5)

  const activeProfile = AGE_PROFILES.find((p) => p.value === ageProfile)!

  const results = useMemo(() => {
    const totalYieldKg = treeCount * activeProfile.yield
    const totalYieldTons = totalYieldKg / 1000
    const pricePerTon = priceUnit === 'kg' ? marketPrice * 1000 : marketPrice
    const revenue = totalYieldTons * pricePerTon
    const revenuePerTree = treeCount > 0 ? revenue / treeCount : 0
    return { totalYieldKg, totalYieldTons, revenue, revenuePerTree }
  }, [treeCount, ageProfile, marketPrice, priceUnit, activeProfile.yield])

  // Revenue bar relative to max hypothetical
  const maxRevenue = treeCount * 220 * (priceUnit === 'kg' ? marketPrice * 1000 : marketPrice) / 1000
  const revenuePct = maxRevenue > 0 ? Math.min((results.revenue / maxRevenue) * 100, 100) : 0

  const ageBarWidths: Record<AgeProfile, number> = { young: 25 / 220 * 100, mature: 100 / 220 * 100, peak: 100 }

  return (
    <div>
      <SectionHeader
        badge="Calculator 3"
        h2="Mango Crop Yield & Revenue Forecaster"
        description="Project total harvest weight and estimated market revenue based on tree count, age profile, and expected market prices."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <div
          className="rounded-3xl border p-6"
          style={{ background: '#fff', borderColor: '#e8d9b0' }}
        >
          <h3 className="text-base font-bold mb-4" style={{ color: '#44403c' }}>🌾 Yield Parameters</h3>

          <NumberField
            label="Total Tree Count"
            value={treeCount}
            min={1}
            step={10}
            unit="trees"
            onChange={setTreeCount}
            tooltip="Use the value from the Spacing Calculator or enter your existing tree inventory count."
          />

          <SelectField
            label="Tree Age Profile"
            value={ageProfile}
            options={AGE_PROFILES.map((p) => ({ value: p.value, label: p.label }))}
            onChange={(v) => setAgeProfile(v as AgeProfile)}
            tooltip="Yield per tree (FAO tropical fruit benchmarks): Young (3–5yr)=25kg, Mature (6–10yr)=100kg, Peak (11–40yr)=220kg per tree annually."
          />

          {/* Age yield comparison bars */}
          <div
            className="mt-2 mb-4 p-4 rounded-2xl border space-y-3"
            style={{ background: '#fafaf9', borderColor: '#e8d9b0' }}
          >
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#a16207' }}>
              Yield per Tree by Age
            </p>
            {AGE_PROFILES.map((p) => (
              <div key={p.value} className="flex items-center gap-3">
                <span className="text-xs w-28 flex-shrink-0 font-medium" style={{ color: p.value === ageProfile ? p.color : '#78716c' }}>
                  {p.label.split(' ')[0]} {p.label.split(' ')[1] || ''}
                </span>
                <div className="flex-1 bg-stone-100 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${ageBarWidths[p.value as AgeProfile]}%`, background: p.color }}
                  />
                </div>
                <span className="text-xs font-bold w-14 text-right" style={{ color: p.color }}>
                  {p.yield} kg
                </span>
              </div>
            ))}
          </div>

          <div className="flex gap-3 items-end">
            <div className="w-40 flex-shrink-0">
              <SelectField
                label="Price Unit"
                value={priceUnit}
                options={PRICE_UNIT_OPTIONS}
                onChange={(v) => setPriceUnit(v as PriceUnit)}
              />
            </div>
            <div className="flex-1">
              <NumberField
                label={`Market Price (${priceUnit === 'kg' ? '$/kg' : '$/ton'})`}
                value={marketPrice}
                min={0.01}
                step={0.1}
                unit={priceUnit === 'kg' ? '$/kg' : '$/t'}
                onChange={setMarketPrice}
                tooltip="Use current farmgate or export market prices. Premium cultivars (Alphonso, Carabao) command 2–5x standard prices."
              />
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {/* Hero revenue card */}
          <div
            className="rounded-3xl p-6 border"
            style={{
              background: 'linear-gradient(135deg, #7c2d12 0%, #9a3412 50%, #b45309 100%)',
              borderColor: '#d97706',
            }}
          >
            <p className="text-sm font-bold uppercase tracking-widest mb-1 text-amber-200">
              Total Projected Harvest Value
            </p>
            <p className="text-5xl font-extrabold tracking-tight text-white leading-none">
              {formatCurrency(results.revenue)}
            </p>
            <p className="text-sm text-amber-300 mt-2">
              {formatCurrency(results.revenuePerTree)} per tree avg.
            </p>

            {/* Revenue gauge bar */}
            <div className="mt-5">
              <div className="flex justify-between text-xs text-amber-200 mb-1.5">
                <span>Young Yield</span>
                <span>Peak Yield</span>
              </div>
              <div className="h-3 rounded-full bg-amber-900 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${revenuePct}%`,
                    background: 'linear-gradient(to right, #fbbf24, #f59e0b)',
                  }}
                />
              </div>
              <p className="text-xs text-amber-300 mt-1.5 text-center">
                {revenuePct.toFixed(0)}% of maximum yield potential
              </p>
            </div>
          </div>

          {/* Yield metrics */}
          <div className="grid grid-cols-2 gap-3">
            <div
              className="rounded-2xl border p-4 text-center"
              style={{ background: '#fff', borderColor: '#e8d9b0' }}
            >
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#a16207' }}>
                Total Yield
              </p>
              <p className="text-2xl font-bold" style={{ color: '#1c1917' }}>
                {results.totalYieldKg.toLocaleString()}
              </p>
              <p className="text-sm" style={{ color: '#78716c' }}>kilograms</p>
            </div>
            <div
              className="rounded-2xl border p-4 text-center"
              style={{ background: '#fff', borderColor: '#e8d9b0' }}
            >
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#a16207' }}>
                Total Yield
              </p>
              <p className="text-2xl font-bold" style={{ color: '#1c1917' }}>
                {results.totalYieldTons.toFixed(2)}
              </p>
              <p className="text-sm" style={{ color: '#78716c' }}>metric tons</p>
            </div>
          </div>

          {/* Active profile detail */}
          <div
            className="rounded-2xl border p-4"
            style={{ background: '#fff7ed', borderColor: '#fdba74' }}
          >
            <p className="text-sm font-bold mb-2" style={{ color: '#9a3412' }}>📊 Active Profile</p>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div>
                <p className="font-bold text-lg" style={{ color: activeProfile.color }}>{activeProfile.yield}kg</p>
                <p className="text-xs" style={{ color: '#78716c' }}>per tree</p>
              </div>
              <div>
                <p className="font-bold text-lg" style={{ color: '#d97706' }}>{treeCount.toLocaleString()}</p>
                <p className="text-xs" style={{ color: '#78716c' }}>trees</p>
              </div>
              <div>
                <p className="font-bold text-lg" style={{ color: '#b45309' }}>
                  {priceUnit === 'kg' ? `$${marketPrice}/kg` : `$${marketPrice}/t`}
                </p>
                <p className="text-xs" style={{ color: '#78716c' }}>market price</p>
              </div>
            </div>
          </div>

          <AgronomistNote
            text="Best practice: Yield significantly decreases when pruning is neglected or drip irrigation is inadequate. Fertilizer, pest management, and thinning programs are essential to achieve benchmark yields. These figures represent well-managed orchards."
            type="warning"
          />
        </div>
      </div>
    </div>
  )
}
